import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { ScheduleSelect } from "@/components/molecules/schedule-select";
import type { Agent } from "@/features/agents/types";
import type {
  CallDestination,
  CallRoutingAction,
  CallRoutingRule,
  GeoCallFilter,
  OutboundCallFilter,
} from "@/features/connectors/types";
import { useTranslation } from "@/features/utils/i18n";
import { Plus, Trash } from "@phosphor-icons/react";
import {
  DestinationList,
  type RoutableUser,
  type TransferableMember,
} from "../destination-list";
import { GeoFilterField } from "../geo-filter-field";
import { OutboundFilterField } from "../outbound-filter-field";
import { readTransferDestinations, RING_BEFORE_AGENT_MAX_S } from "./state";

interface RoutingRuleCardProps {
  rule: CallRoutingRule;
  index: number;
  agents: Agent[];
  members: TransferableMember[];
  users: RoutableUser[];
  onChange: (id: string, changes: Partial<CallRoutingRule>) => void;
  onRemove: (id: string) => void;
}

/** One "calls matching this pattern go there" rule. */
const RoutingRuleCard = ({
  rule,
  index,
  agents,
  members,
  users,
  onChange,
  onRemove,
}: RoutingRuleCardProps) => {
  const { t } = useTranslation("connectors");

  return (
    <div className="space-y-2 rounded-md border bg-muted/30 p-3">
      <div className="flex items-center justify-between">
        <span className="text-xs font-medium text-muted-foreground">
          {t("connectors.routing.ruleLabel", { index: index + 1 })}
        </span>
        <Button
          type="button"
          size="sm"
          variant="ghost"
          onClick={() => onRemove(rule.id)}
        >
          <Trash className="h-4 w-4" />
        </Button>
      </div>

      <div className="space-y-1">
        <Label className="text-xs">
          {t("connectors.routing.patternLabel")}
        </Label>
        <Input
          value={rule.pattern}
          onChange={(e) => onChange(rule.id, { pattern: e.target.value })}
          placeholder={t("connectors.routing.patternPlaceholder")}
        />
        <p className="text-xs text-muted-foreground">
          {t("connectors.routing.patternHint")}
        </p>
      </div>

      <div className="space-y-1">
        <Label className="text-xs">{t("connectors.routing.actionLabel")}</Label>
        <Select
          value={rule.action}
          onValueChange={(value) =>
            onChange(rule.id, {
              action: value as CallRoutingAction,
              agent_id: undefined,
              transfer_member_id: undefined,
              transfer_number: undefined,
            })
          }
        >
          <SelectTrigger>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="agent">
              {t("connectors.routing.actions.agent")}
            </SelectItem>
            <SelectItem value="transfer">
              {t("connectors.routing.actions.transfer")}
            </SelectItem>
            <SelectItem value="reject">
              {t("connectors.routing.actions.reject")}
            </SelectItem>
          </SelectContent>
        </Select>
      </div>

      {rule.action === "agent" && (
        <div className="space-y-1">
          <Label className="text-xs">
            {t("connectors.routing.agentLabel")}
          </Label>
          <Select
            value={rule.agent_id || ""}
            onValueChange={(value) => onChange(rule.id, { agent_id: value })}
          >
            <SelectTrigger>
              <SelectValue
                placeholder={t("connectors.routing.agentPlaceholder")}
              />
            </SelectTrigger>
            <SelectContent>
              {agents.map((agent) => (
                <SelectItem key={agent.id} value={agent.id!}>
                  {agent.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}

      {rule.action === "transfer" && (
        <DestinationList
          value={readTransferDestinations(rule)}
          onChange={(destinations) =>
            onChange(rule.id, {
              transfer_destinations: destinations,
              // Reading the legacy pair promoted it into the list above;
              // leaving it set would make the rule say two different things.
              transfer_member_id: undefined,
              transfer_number: undefined,
            })
          }
          members={members}
          users={users}
          idPrefix={`rule-${rule.id}`}
          title={t("connectors.routing.destinations.transferTitle")}
          description={t("connectors.routing.destinations.transferDescription")}
        />
      )}
    </div>
  );
};

/** A tick-box with its explanation, for the two platform-staff switches. */
const StaffToggle = ({
  id,
  checked,
  onChange,
  label,
  description,
}: {
  id: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
  label: string;
  description: string;
}) => (
  <div className="space-y-2 border-t pt-3">
    <div className="flex items-start space-x-2">
      <Checkbox
        id={id}
        checked={checked}
        onCheckedChange={(value) => onChange(value === true)}
      />
      <Label htmlFor={id} className="text-sm font-normal cursor-pointer">
        {label}
      </Label>
    </div>
    <p className="text-xs text-muted-foreground">{description}</p>
  </div>
);

interface RoutingPanelProps {
  agents: Agent[];
  members: TransferableMember[];
  users: RoutableUser[];
  isSysadmin: boolean;
  routingRules: CallRoutingRule[];
  onRuleChange: (id: string, changes: Partial<CallRoutingRule>) => void;
  onRuleAdd: () => void;
  onRuleRemove: (id: string) => void;
  fallbackDestinations: CallDestination[];
  onFallbackChange: (destinations: CallDestination[]) => void;
  agentOffEnabled: boolean;
  onAgentOffEnabledChange: (value: boolean) => void;
  agentOffScheduleId: string;
  onAgentOffScheduleChange: (scheduleId: string) => void;
  ringBeforeAgentSeconds: number;
  onRingBeforeAgentSecondsChange: (seconds: number) => void;
  geoFilter: GeoCallFilter;
  onGeoFilterChange: (filter: GeoCallFilter) => void;
  outboundFilter: OutboundCallFilter;
  onOutboundFilterChange: (filter: OutboundCallFilter) => void;
  demoNumber: boolean;
  onDemoNumberChange: (value: boolean) => void;
  codeRouting: boolean;
  onCodeRoutingChange: (value: boolean) => void;
  defaultConnector: boolean;
  onDefaultConnectorChange: (value: boolean) => void;
}

/**
 * Everything that decides where a call goes: the per-caller rules, the fallback
 * chain behind them, which countries may reach or be reached, and the two
 * platform-wide switches only staff may flip.
 */
export const RoutingPanel = ({
  agents,
  members,
  users,
  isSysadmin,
  routingRules,
  onRuleChange,
  onRuleAdd,
  onRuleRemove,
  fallbackDestinations,
  onFallbackChange,
  agentOffEnabled,
  onAgentOffEnabledChange,
  agentOffScheduleId,
  onAgentOffScheduleChange,
  ringBeforeAgentSeconds,
  onRingBeforeAgentSecondsChange,
  geoFilter,
  onGeoFilterChange,
  outboundFilter,
  onOutboundFilterChange,
  demoNumber,
  onDemoNumberChange,
  codeRouting,
  onCodeRoutingChange,
  defaultConnector,
  onDefaultConnectorChange,
}: RoutingPanelProps) => {
  const { t } = useTranslation("connectors");

  return (
    <div className="space-y-3 rounded-lg border p-4">
      <div className="space-y-1">
        <Label className="text-sm font-semibold">
          {t("connectors.routing.title")}
        </Label>
        <p className="text-xs text-muted-foreground">
          {t("connectors.routing.description")}
        </p>
      </div>

      {routingRules.length === 0 && (
        <p className="text-xs italic text-muted-foreground">
          {t("connectors.routing.empty")}
        </p>
      )}

      {routingRules.map((rule, index) => (
        <RoutingRuleCard
          key={rule.id}
          rule={rule}
          index={index}
          agents={agents}
          members={members}
          users={users}
          onChange={onRuleChange}
          onRemove={onRuleRemove}
        />
      ))}

      <Button type="button" variant="outline" size="sm" onClick={onRuleAdd}>
        <Plus className="h-4 w-4" />
        {t("connectors.routing.addRule")}
      </Button>

      <div className="space-y-2 border-t pt-3">
        <DestinationList
          value={fallbackDestinations}
          onChange={onFallbackChange}
          members={members}
          users={users}
          idPrefix="fallback"
          title={t("connectors.routing.fallback.title")}
          description={t("connectors.routing.fallback.description")}
        />
      </div>

      {/* Agent-off window: the fallback above takes every call on a schedule. */}
      <div className="space-y-2 border-t pt-3">
        <div className="flex items-start space-x-2">
          <Checkbox
            id="agent-off"
            checked={agentOffEnabled}
            onCheckedChange={(value) => onAgentOffEnabledChange(value === true)}
          />
          <Label
            htmlFor="agent-off"
            className="text-sm font-normal cursor-pointer"
          >
            {t("connectors.routing.agentOff.label")}
          </Label>
        </div>
        <p className="text-xs text-muted-foreground">
          {t("connectors.routing.agentOff.description")}
        </p>
        {agentOffEnabled && (
          <ScheduleSelect
            value={agentOffScheduleId}
            allowNone={false}
            placeholder={t("connectors.routing.agentOff.schedulePlaceholder")}
            onChange={onAgentOffScheduleChange}
          />
        )}
      </div>

      {/* The team first, then the agent: applies to the window above and to
          "transfer" rules (backend connectors/ring-before-agent.ts). */}
      <div className="space-y-2 border-t pt-3">
        <Label htmlFor="ring-before-agent" className="text-sm font-normal">
          {t("connectors.routing.ringBeforeAgent.label")}
        </Label>
        <div className="flex items-center gap-2">
          <Input
            id="ring-before-agent"
            type="number"
            inputMode="numeric"
            min={0}
            max={RING_BEFORE_AGENT_MAX_S}
            step={1}
            className="w-24"
            value={ringBeforeAgentSeconds}
            onChange={(event) =>
              onRingBeforeAgentSecondsChange(
                Math.max(0, Number(event.target.value) || 0),
              )
            }
          />
          <span className="text-sm text-muted-foreground">
            {t("connectors.routing.ringBeforeAgent.unit")}
          </span>
        </div>
        <p className="text-xs text-muted-foreground">
          {t("connectors.routing.ringBeforeAgent.description")}
        </p>
      </div>

      <GeoFilterField
        value={geoFilter}
        onChange={onGeoFilterChange}
        readOnly={!isSysadmin}
      />

      <OutboundFilterField
        value={outboundFilter}
        onChange={onOutboundFilterChange}
      />

      {/* Shared demo number — platform staff only. */}
      {isSysadmin && (
        <StaffToggle
          id="demo-number"
          checked={demoNumber}
          onChange={onDemoNumberChange}
          label={t(
            "connectors.routing.demoNumber.label",
            "Utiliser comme numéro de démonstration",
          )}
          description={t(
            "connectors.routing.demoNumber.description",
            "Les appels entrants sont comparés aux numéros déclarés par les connecteurs « Numéro temporaire » des autres comptes : si le numéro appelant correspond, l'appel bascule sur l'agent de ce compte. Sinon, l'agent de ce connecteur répond (instructions).",
          )}
        />
      )}

      {/* Code routing works on a demo number only — platform staff only. */}
      {isSysadmin && demoNumber && (
        <StaffToggle
          id="code-routing"
          checked={codeRouting}
          onChange={onCodeRoutingChange}
          label={t(
            "connectors.routing.codeRouting.label",
            "Router vers les espaces de démo par code",
          )}
          description={t(
            "connectors.routing.codeRouting.description",
            "L'agent de ce numéro demande le code à 6 chiffres imprimé sur le flyer et passe l'appel à l'agent de l'espace de démo correspondant. Ce numéro devient celui de l'export des espaces de démo.",
          )}
        />
      )}

      {/* Catch-all for the numbers nobody owns — platform staff only. */}
      {isSysadmin && (
        <StaffToggle
          id="default-connector"
          checked={defaultConnector}
          onChange={onDefaultConnectorChange}
          label={t("connectors.routing.defaultConnector.label")}
          description={t("connectors.routing.defaultConnector.description")}
        />
      )}
    </div>
  );
};
